import { useEffect } from "react";
import { useSnapshot } from "valtio";
import {
  organizationSegmentsStore,
  resetOrganizationSegmentsStore,
} from "../segmentsStore";
import { OrganizationService } from "@services/organizations/OrganizationService";
import { CreatePopulationSegmentPayload } from "@services/organizations/commands/cmdCreatePopulationSegment";
import { UpdatePopulationSegmentPayload } from "@services/organizations/commands/cmdUpdatePopulationSegment";
import showAlert from "src/shared/utils/generic.tsx";

const service = new OrganizationService();

const useOrganizationSegments = (organizationId: number) => {
  const snap = useSnapshot(organizationSegmentsStore);

  useEffect(() => {
    if (!organizationId) return;
    if (organizationId !== organizationSegmentsStore.organizationId) {
      resetOrganizationSegmentsStore();
      organizationSegmentsStore.organizationId = organizationId;
    }
    loadSegments(organizationId);
  }, [organizationId]);

  async function loadSegments(orgId: number) {
    organizationSegmentsStore.isLoading = true;
    organizationSegmentsStore.error = null;
    try {
      organizationSegmentsStore.segments = await service.getPopulationSegments(orgId);
    } catch {
      organizationSegmentsStore.error = "Failed to load population segments.";
    } finally {
      organizationSegmentsStore.isLoading = false;
    }
  }

  async function createSegment(payload: CreatePopulationSegmentPayload) {
    organizationSegmentsStore.isSaving = true;
    try {
      await service.createPopulationSegment(organizationId, payload);
      showAlert("Segment created successfully", "success", 3000);
      await loadSegments(organizationId);
    } catch (err) {
      // Surface the server's message (e.g. duplicate-name 409) rather than a generic one.
      showAlert(err instanceof Error ? err.message : "Failed to create segment", "error", 4000);
      throw err;
    } finally {
      organizationSegmentsStore.isSaving = false;
    }
  }

  async function updateSegment(segmentId: number, payload: UpdatePopulationSegmentPayload) {
    organizationSegmentsStore.isSaving = true;
    try {
      await service.updatePopulationSegment(organizationId, segmentId, payload);
      showAlert("Segment updated successfully", "success", 3000);
      await loadSegments(organizationId);
    } catch (err) {
      showAlert(err instanceof Error ? err.message : "Failed to update segment", "error", 4000);
      throw err;
    } finally {
      organizationSegmentsStore.isSaving = false;
    }
  }

  async function deleteSegment(segmentId: number) {
    organizationSegmentsStore.isSaving = true;
    try {
      await service.deletePopulationSegment(organizationId, segmentId);
      showAlert("Segment deleted successfully", "success", 3000);
      await loadSegments(organizationId);
    } catch (err) {
      // e.g. 409 "still in use by N launch(es)".
      showAlert(err instanceof Error ? err.message : "Failed to delete segment", "error", 4000);
      throw err;
    } finally {
      organizationSegmentsStore.isSaving = false;
    }
  }

  return {
    segments: snap.segments,
    isLoading: snap.isLoading,
    isSaving: snap.isSaving,
    error: snap.error,
    createSegment,
    updateSegment,
    deleteSegment,
    // For hosts whose actions change launch counts (e.g. linking a campaign
    // from the population page) — the warned-but-allowed shared-population
    // rule reads launch_count, which otherwise goes stale for the session.
    reloadSegments: () => loadSegments(organizationId),
  };
};

export default useOrganizationSegments;
